import { useEffect, useRef, useCallback } from 'react';
import type { CarouselApi } from '@/components/ui/carousel';

interface UseCarouselEdgeScrollOptions {
  api: CarouselApi | undefined;
  isDragging: boolean;
  edgeThreshold?: number; // pixels from edge to trigger scroll
  scrollInterval?: number; // ms between scrolls
}

export function useCarouselEdgeScroll({
  api,
  isDragging,
  edgeThreshold = 80,
  scrollInterval = 400,
}: UseCarouselEdgeScrollOptions) {
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollDirection = useRef<'left' | 'right' | null>(null);

  const clearScrollInterval = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    lastScrollDirection.current = null;
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent | TouchEvent) => {
    if (!isDragging || !api) {
      clearScrollInterval();
      return;
    }

    // Get the x position from either pointer or touch event
    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0]?.clientX ?? 0;
    } else {
      clientX = e.clientX;
    }

    const screenWidth = window.innerWidth;
    const leftEdge = edgeThreshold;
    const rightEdge = screenWidth - edgeThreshold;

    let direction: 'left' | 'right' | null = null;

    if (clientX < leftEdge) {
      direction = 'left';
    } else if (clientX > rightEdge) {
      direction = 'right';
    }

    // If direction changed or we're not in an edge zone, clear existing interval
    if (direction !== lastScrollDirection.current) {
      clearScrollInterval();
      lastScrollDirection.current = direction;

      if (direction) {
        // Start new scroll interval
        const scroll = () => {
          if (direction === 'left' && api.canScrollPrev()) {
            api.scrollPrev();
          } else if (direction === 'right' && api.canScrollNext()) {
            api.scrollNext();
          }
        };

        // Immediate first scroll
        scroll();

        // Then continue at interval
        scrollIntervalRef.current = setInterval(scroll, scrollInterval);
      }
    }
  }, [isDragging, api, edgeThreshold, scrollInterval, clearScrollInterval]);

  useEffect(() => {
    if (!isDragging) {
      clearScrollInterval();
      return;
    }

    // Listen for both pointer and touch move events
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove as any);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove as any);
      clearScrollInterval();
    };
  }, [isDragging, handlePointerMove, clearScrollInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearScrollInterval();
  }, [clearScrollInterval]);
}
